import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Badge, Banner, useApiClient, useQuery } from '@acc/web-shared';
import { AGENCY_ROLES, ROLE_LABELS, type AgencyRole } from '@acc/domain';
import { PageHead, useAgency, type TeamMember } from '../agency';

interface Former extends TeamMember { openQuotes: number; openCalls: number }

export function Team() {
  const a = useAgency();
  const api = useApiClient();
  const owner = a.me.role === 'AgencyOwner';
  const former = useQuery<{ members: Former[] }>(a.manager ? a.path('/team/former') : null);
  const [busy, setBusy] = useState('');
  // Optimistic: the checkbox flips at once and reverts if the save fails.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  async function toggle(membershipId: string, seesAllQuotes: boolean) {
    setBusy(membershipId);
    setPending((p) => ({ ...p, [membershipId]: seesAllQuotes }));
    try { await api(a.path(`/team/${membershipId}/visibility`), { method: 'PATCH', body: { seesAllQuotes } }); a.reloadTeam(); }
    catch (err) { setPending((p) => { const n = { ...p }; delete n[membershipId]; return n; }); a.notify('bad', (err as Error).message); }
    finally { setBusy(''); }
  }
  async function change(m: TeamMember, body: { role?: AgencyRole; status?: 'Active' | 'Disabled' }, done: string) {
    setBusy(m.membershipId);
    try { await api(a.path(`/team/${m.membershipId}`), { method: 'PATCH', body }); a.reloadTeam(); former.reload(); a.notify('ok', done); }
    catch (err) { a.notify('bad', (err as Error).message); }
    finally { setBusy(''); }
  }
  // Office managers manage employee accounts; the owner manages everyone (baseline 9.7).
  const manages = (m: TeamMember) => a.manager && m.membershipId !== a.me.membershipId && (owner || m.role === 'Employee');
  const nameOf = (m: TeamMember) => m.displayName ?? m.email;

  return (
    <>
      <PageHead title="Team" sub={a.manager ? 'Invite people, choose which employees see every quote, and suspend or deactivate accounts.' : 'People in your agency.'} />
      {a.manager && <Invite owner={owner} onDone={() => a.reloadTeam()} />}
      <div className="card">
        <table className="data">
          <thead><tr><th>Person</th><th>Role</th><th>Status</th><th>Quote visibility</th>{a.manager && <th />}</tr></thead>
          <tbody>
            {a.team.map((m) => (
              <tr key={m.membershipId}>
                <td><div>{nameOf(m)}</div>{m.displayName && <div className="muted" style={{ fontSize: 13 }}>{m.email}</div>}</td>
                <td>
                  {owner && m.membershipId !== a.me.membershipId ? (
                    <select className="inline" aria-label={`Role for ${nameOf(m)}`} value={m.role} disabled={busy === m.membershipId} onChange={(e) => void change(m, { role: e.target.value as AgencyRole }, `${nameOf(m)} is now ${ROLE_LABELS[e.target.value as AgencyRole]}.`)}>
                      {AGENCY_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  ) : ROLE_LABELS[m.role]}
                </td>
                <td><Badge status={m.status} /></td>
                <td>
                  {m.role !== 'Employee' ? <span className="muted">All quotes</span> : a.manager ? (
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                      <input type="checkbox" checked={pending[m.membershipId] ?? m.seesAllQuotes} onChange={(e) => void toggle(m.membershipId, e.target.checked)} aria-label={`${nameOf(m)} sees all quotes`} />
                      Sees all quotes
                    </label>
                  ) : <span className="muted">{m.seesAllQuotes ? 'All quotes' : 'Own quotes only'}</span>}
                </td>
                {a.manager && (
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {manages(m) && (
                      <>
                        <button type="button" className="btn small" disabled={busy === m.membershipId}
                          onClick={() => { if (window.confirm(`Suspend ${nameOf(m)} now? They lose access immediately. Their open work stays with them until you reassign it.`)) void change(m, { status: 'Disabled' }, `${nameOf(m)} suspended. Reassign their open work under Inactive staff.`); }}>Suspend</button>{' '}
                        <Link className="btn small danger" to={`${a.base}/team/${m.membershipId}/offboard`}>Deactivate…</Link>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {a.manager && former.data && former.data.members.length > 0 && (
        <div className="card">
          <h2>Inactive staff</h2>
          <p className="sub">Suspended or deactivated. Their history stays credited to them.</p>
          <table className="data">
            <thead><tr><th>Person</th><th>Role</th><th>Open quotes</th><th>Open calls</th><th /></tr></thead>
            <tbody>
              {former.data.members.map((m) => (
                <tr key={m.membershipId}>
                  <td>{nameOf(m)}</td>
                  <td>{ROLE_LABELS[m.role]}</td>
                  <td>{m.openQuotes}</td>
                  <td>{m.openCalls}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {manages(m) && (
                      <>
                        {m.openQuotes + m.openCalls > 0 && <><Link className="btn small primary" to={`${a.base}/team/${m.membershipId}/offboard`}>Reassign work…</Link>{' '}</>}
                        <button type="button" className="btn small" disabled={busy === m.membershipId} onClick={() => void change(m, { status: 'Active' }, `${nameOf(m)} reactivated.`)}>Reactivate</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Invite({ owner, onDone }: { owner: boolean; onDone: () => void }) {
  const a = useAgency();
  const api = useApiClient();
  const [f, setF] = useState({ email: '', displayName: '', role: 'Employee' as AgencyRole });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setNote(null);
    try {
      const r = await api<{ alreadyMember: boolean; invitation: { status: 'sent' } | { status: 'manual'; reason: string } }>(a.path('/team/invitations'), { method: 'POST', body: f });
      if (r.alreadyMember) setNote({ tone: 'warn', text: `${f.email} is already on the team.` });
      else if (r.invitation.status === 'sent') setNote({ tone: 'ok', text: `Invitation sent to ${f.email}. They appear as Invited until they first sign in.` });
      else setNote({ tone: 'warn', text: `${f.email} was added as Invited, but the Microsoft invitation e-mail could not be sent (${r.invitation.reason}). Ask ACC administration to send it.` });
      setF({ email: '', displayName: '', role: 'Employee' });
      onDone();
    } catch (err) { setNote({ tone: 'bad', text: (err as Error).message }); } finally { setBusy(false); }
  }
  return (
    <form className="card" onSubmit={submit} aria-labelledby="invite">
      <h2 id="invite">Invite someone</h2>
      {note && <Banner tone={note.tone}>{note.text}</Banner>}
      <div className="form-grid">
        <label className="field">E-mail<input required type="email" maxLength={320} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
        <label className="field">Name<input maxLength={200} value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} /></label>
        <label className="field">Role
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as AgencyRole })}>
            {(owner ? AGENCY_ROLES : (['Employee'] as const)).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </label>
      </div>
      <div className="actions"><button className="btn primary" type="submit" disabled={busy}>{busy ? 'Inviting…' : 'Send invitation'}</button></div>
    </form>
  );
}
